import { useEffect, useState } from "react";
import { X, Download, Heart } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";

export const OutcomeBanner = () => {
  const { t, user, ready } = useApp();
  const [prompt, setPrompt] = useState(null);
  useEffect(() => {
    if (!ready) return;
    api.get("/outcome-prompt").then((r) => setPrompt(r.data.prompt)).catch(() => {});
  }, [ready, user?.id]);
  if (!prompt) return null;
  const answer = (response) => { api.post(`/outcome-prompt/${prompt.contact_event_id}`, { response }).catch(() => {}); setPrompt(null); };
  return (
    <div data-testid="outcome-banner" className="rise max-w-6xl mx-auto px-4 mt-3">
      <div className="rounded-2xl bg-[#FFFDF9] border-2 border-[#F59E0B]/60 card-shadow p-4 relative">
        <button data-testid="outcome-dismiss" aria-label={t("dismiss")} onClick={() => answer("dismissed")} className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F3EDE4]"><X className="w-4 h-4" /></button>
        <p className="font-bold pr-10">{t("outcome_q", { name: prompt.poster_name, title: prompt.title })}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {["yes", "no", "still_talking"].map((r) => (
            <button key={r} data-testid={`outcome-${r}`} onClick={() => answer(r)} className={`press min-h-[44px] px-4 rounded-full font-bold text-sm ${r === "yes" ? "bg-[#1E4D3B] text-white" : "bg-[#F3EDE4]"}`}>{t(r)}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export const InstallBanner = () => {
  const { t, user, ready } = useApp();
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(() => !!sessionStorage.getItem("dm_install_later"));
  const [installed, setInstalled] = useState(() => !!localStorage.getItem("dm_installed"));
  const [visits, setVisits] = useState(0);
  useEffect(() => {
    const standalone = window.matchMedia("(display-mode: standalone)").matches || navigator.standalone;
    if (standalone) { localStorage.setItem("dm_installed", "1"); setInstalled(true); }
    if (!sessionStorage.getItem("dm_counted")) {
      sessionStorage.setItem("dm_counted", "1");
      localStorage.setItem("dm_visits", String(Number(localStorage.getItem("dm_visits") || 0) + 1));
    }
    setVisits(Number(localStorage.getItem("dm_visits")));
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { localStorage.setItem("dm_installed", "1"); setInstalled(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  if (!ready || installed || hidden || (!user && visits < 2)) return null;
  const later = () => { sessionStorage.setItem("dm_install_later", "1"); setHidden(true); };
  const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const install = async () => {
    if (!deferred) { toast(t("android_install"), { duration: 7000 }); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") { localStorage.setItem("dm_installed", "1"); setInstalled(true); } else later();
  };
  return (
    <div data-testid="install-banner" className="rise fixed bottom-24 inset-x-3 z-40 max-w-md mx-auto rounded-2xl bg-[#1E4D3B] text-white p-3 shadow-2xl">
      <div className="flex items-center gap-3">
        <img src="/icon.svg" alt="" className="w-10 h-10 rounded-xl shrink-0" />
        <div className="min-w-0"><div className="font-bold leading-snug">{t("install_title")}</div><div className="text-xs text-white/80">{ios && !deferred ? t("ios_install") : t("install_body")}</div></div>
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button data-testid="install-dismiss" onClick={later} className="press min-h-[44px] px-4 rounded-full text-sm font-bold text-white/90 hover:bg-white/10">{t("install_later")}</button>
        {(deferred || !ios) && <button data-testid="install-accept" onClick={install} className="press min-h-[44px] px-4 rounded-full bg-[#F59E0B] text-[#291E18] text-sm font-bold inline-flex items-center gap-1"><Download className="w-4 h-4" />{t("install")}</button>}
      </div>
    </div>
  );
};

export const WelcomeBanner = () => {
  const { t, user, ready, setAuth } = useApp();
  const params = new URLSearchParams(window.location.search);
  const [invited] = useState(() => {
    const ref = params.get("ref");
    if (ref && /^[A-Za-z0-9]{4,12}$/.test(ref)) { localStorage.setItem("dm_ref", ref.toUpperCase()); return true; }
    return false;
  });
  const [show, setShow] = useState(() => (invited || params.get("via") === "share") && !localStorage.getItem("dm_welcomed"));
  useEffect(() => {
    const u = new URL(window.location.href);
    if (!u.searchParams.has("via") && !u.searchParams.has("ref")) return;
    if (show) localStorage.setItem("dm_welcomed", "1");
    u.searchParams.delete("via"); u.searchParams.delete("ref"); window.history.replaceState(null, "", u.pathname + u.search + u.hash);
  }, [show]);
  if (!show || !ready || user) return null;
  return (
    <div data-testid="welcome-banner" className="rise max-w-6xl mx-auto px-4 mt-3">
      <div className="relative rounded-[20px] bg-[#1E4D3B] text-white p-5 card-shadow overflow-hidden">
        <div className="absolute -right-6 -bottom-6 w-28 h-28 rounded-full ankara-dots opacity-30" aria-hidden="true" />
        <button data-testid="welcome-dismiss" aria-label={t("dismiss")} onClick={() => setShow(false)} className="absolute top-2 right-2 w-11 h-11 flex items-center justify-center rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
        <p className="text-xl font-extrabold pr-10 inline-flex items-center gap-2"><Heart className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" aria-hidden="true" />{t("welcome_title")}</p>
        <p className="mt-1 text-white/90" data-testid="welcome-body">{t(invited ? "welcome_invited" : "welcome_body")}</p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button data-testid="welcome-join" onClick={() => { setShow(false); setAuth({ mode: "signup" }); }} className="press min-h-[48px] px-5 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{t("welcome_cta")}</button>
          <button data-testid="welcome-browse" onClick={() => setShow(false)} className="press min-h-[48px] px-5 rounded-full border-2 border-white/80 font-bold">{t("welcome_browse")}</button>
        </div>
      </div>
    </div>
  );
};
